import type { TUniversity } from '../../types';
import type { FC } from 'react';
import { Edit2, Global, Location, Trash } from 'iconsax-react';
import {
  EditableInputCell,
  EditableInputCellEditMode,
  EditableInputCellViewMode,
} from '@src/shared/components/editable-input-cell';
import { Badge } from '@src/design-system/badge';
import { SingleDeleteAction } from '@src/design-system/molecules/ticlick-table/actions';
import universitiesApi from '../../services/universities-api';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@src/design-system/dialog';
import CreateUniversityModal from '../create-university-modal';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from '@src/design-system/popover';
import { cn } from '@src/design-system/helpers';
import { ChevronDown } from 'lucide-react';
import universityStatusesApi from '../../services/university-statuses-api';
import { InfiniteList } from '@src/design-system/molecules/infinite-list';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';

const ViewUniversityHeader: FC<{ university: TUniversity }> = ({
  university,
}) => {
  const { mutateAsync: deleteUniversity, isPending } =
    universitiesApi.useDelete();
  const { mutate: updateUniversity } = universitiesApi.useUpdate();

  const addressLine =
    university?.address_line_1 ?? university?.address_line_2 ?? '';
  const addressTooltip = [
    university?.address_line_1,
    university?.address_line_2,
  ]
    .filter((line) => line?.trim())
    .join(', ');

  return (
    <div className='p-4 rounded-t-xl space-y-10'>
      <div className='flex items-center justify-between'>
        <span className='text-gray-900 font-medium text-lg'>
          {university?.name}
        </span>
        <div className='flex items-center gap-2'>
          <SingleDeleteAction
            id={university.id}
            label='university'
            isLoading={isPending}
            onConfirm={(closeModal) =>
              deleteUniversity({ id: university.id }, { onSuccess: closeModal })
            }
            triggerComponent={
              <div className='group flex items-center cursor-pointer border p-2 border-primary-200 rounded-md'>
                <span className='text-primary-1000 group-hover:mr-1 font-medium text-[11px] opacity-0 -translate-x-1 max-w-0 overflow-hidden transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:max-w-[60px]'>
                  Delete
                </span>
                <Trash
                  className='text-primary-1000'
                  size={16}
                />
              </div>
            }
          />
          <Dialog>
            <DialogTrigger className='cursor-pointer'>
              <div className='group flex items-center cursor-pointer border p-2 border-primary-200 rounded-md'>
                <span className='text-primary-1000 group-hover:mr-1 font-medium text-[11px] opacity-0 -translate-x-1 max-w-0 overflow-hidden transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:max-w-[60px]'>
                  Edit
                </span>
                <Edit2
                  className='text-primary-1000'
                  size={16}
                />
              </div>
            </DialogTrigger>
            <DialogContent className='max-w-[600px]'>
              <CreateUniversityModal university={university} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Avatar className='h-20! w-20! rounded-lg border border-primary-200'>
            <AvatarImage
              src={university?.logo?.file ?? undefined}
              alt={university?.name}
            />
            <AvatarFallback
              colorId={university?.id ?? 0}
              className='rounded-lg'
            >
              <span className='text-white text-2xl font-medium'>
                {university?.name.slice(0, 2).toUpperCase()}
              </span>
            </AvatarFallback>
          </Avatar>
          <div className='flex flex-col gap-1'>
            <span className='text-gray-900 font-medium text-lg'>
              {university?.name}
            </span>
            <div className='flex items-center gap-6'>
              {(university?.address_line_1 || university?.address_line_2) && (
                <div className='flex items-center gap-0.5 min-w-0 max-w-[120px]'>
                  <Location
                    className='text-secondary-1000 shrink-0'
                    size={16}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='text-gray-800 font-normal text-sm truncate min-w-0 cursor-default'>
                        {addressLine}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side='top'
                      className='max-w-xs break-words whitespace-normal'
                    >
                      {addressTooltip || addressLine}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              <div className='flex items-center min-w-0'>
                <Global
                  className='text-secondary-1000 shrink-0'
                  size={16}
                />
                <div className='relative flex-1 min-w-[200px] min-h-[30px]'>
                  <EditableInputCell className='min-w-0'>
                    <EditableInputCellEditMode
                      value={university?.website ?? ''}
                      onBlur={(e) => {
                        const newValue = e.target.value.trim();
                        if (newValue === (university?.website ?? '').trim())
                          return;
                        updateUniversity({
                          id: university.id,
                          website: newValue || undefined,
                        });
                      }}
                    />
                    <EditableInputCellViewMode>
                      <span className='text-gray-800 font-normal text-sm truncate'>
                        {university?.website || 'Enter Website URL'}
                      </span>
                    </EditableInputCellViewMode>
                  </EditableInputCell>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <Popover>
            <PopoverTrigger>
              <Badge
                className='hover:gap-2 hover:pl-2 hover:pr-6 relative group cursor-pointer hover:bg-primary-200 hover:scale-105 transition-all duration-150 text-xs capitalize text-primary-1000 bg-primary-100 px-4 rounded-[8px] min-w-[54px] max-w-[150px] text-wrap truncate min-h-[30px] flex items-center justify-center'
                variant='primaryBlue'
              >
                {university?.status
                  ? university?.status?.name
                  : 'Select Status'}
                <ChevronDown
                  size={16}
                  className='opacity-0 group-hover:opacity-100 absolute right-2 top-1/2 -translate-y-1/2 transition-opacity duration-200 h-full shrink-0 self-center'
                />
              </Badge>
            </PopoverTrigger>
            <PopoverContent
              withPortal={false}
              align='end'
              style={{
                width: '160px',
              }}
              className='p-0 z-50 scrollbar-gray'
            >
              <div className='max-h-[200px] overflow-y-auto p-1'>
                <InfiniteList
                  enableSearch
                  fetchInfiniteData={universityStatusesApi.useFetchInfinite}
                  props={{
                    listProps: { className: 'gap-0 p-0' },
                    listContainerProps: {
                      className: 'p-1 max-h-[200px]',
                    },
                    searchContainerProps: { className: 'p-1 mb-1' },
                    searchInputProps: {
                      placeholder: 'Search in Status',
                      size: 'xs',
                      className: 'text-sm placeholder:text-sm',
                    },
                  }}
                  renderItem={(r) => {
                    return (
                      <PopoverClose
                        key={r.id}
                        className={cn(
                          'flex items-center justify-between capitalize w-full text-sm text-gray-800 p-2 hover:bg-primary-50 rounded-md cursor-pointer',
                          university?.status?.id === r.id &&
                            'bg-primary-50 text-primary-900'
                        )}
                        onClick={() =>
                          updateUniversity({
                            id: university.id,
                            status: r.id,
                          })
                        }
                      >
                        {r.name}
                      </PopoverClose>
                    );
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};

export default ViewUniversityHeader;
